import { useEffect, useMemo, useRef, useState } from "react";
import { FastForward, Gauge, Pause, Play } from "lucide-react";

import Modal from "@/components/Modal";
import { BattleLogFeed } from "@/components/BattleLogFeed";
import { Empty, btn } from "@/components/Page";
import { TRIUMVIRATE_MODE } from "@/components/triumvirate/TriumvirateSectionChrome";

import { LeagueBadge } from "./LeagueBadge";

function deltaText(value) {
  const amount = Number(value || 0);
  return `${amount > 0 ? "+" : ""}${amount}`;
}

function TeamStateStrip({ title, fighters, testId }) {
  return (
    <div data-testid={testId} className="border border-zinc-800 bg-zinc-950/50 rounded-sm p-3">
      <div className="text-xs uppercase tracking-[0.16em] text-zinc-500">{title}</div>
      <div className="mt-2 space-y-2">
        {(fighters || []).map((fighter) => (
          <div key={`${title}-${fighter.gladiator_id}-${fighter.lane}`} className="flex flex-wrap items-center justify-between gap-2 text-sm text-zinc-300">
            <span>{fighter.name} · {fighter.lane}</span>
            <span className={fighter.alive ? "text-emerald-300" : "text-zinc-500"}>
              {fighter.remaining_hp}/{fighter.max_hp}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function TeamStatsTable({ title, fighters, testId }) {
  return (
    <div data-testid={testId} className="border border-zinc-800 bg-zinc-950/55 rounded-sm p-4">
      <div className="text-xs uppercase tracking-[0.16em] text-zinc-500">{title}</div>
      <div className="mt-3 overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="text-zinc-500">
            <tr>
              <th className="py-2 pr-4 text-left font-medium">Fighter</th>
              <th className="py-2 pr-4 text-left font-medium">Lane</th>
              <th className="py-2 pr-4 text-left font-medium">Damage</th>
              <th className="py-2 pr-4 text-left font-medium">Taken</th>
              <th className="py-2 pr-4 text-left font-medium">Kills</th>
              <th className="py-2 pr-4 text-left font-medium">Crits</th>
              <th className="py-2 pr-4 text-left font-medium">Survived</th>
            </tr>
          </thead>
          <tbody>
            {(fighters || []).map((fighter) => (
              <tr key={fighter.gladiator_id} className="border-t border-zinc-900 text-zinc-300">
                <td className="py-2 pr-4 font-cinzel text-amber-200">{fighter.name}</td>
                <td className="py-2 pr-4 uppercase">{fighter.lane}</td>
                <td className="py-2 pr-4 tabular-nums">{fighter.damage_dealt}</td>
                <td className="py-2 pr-4 tabular-nums">{fighter.damage_taken}</td>
                <td className="py-2 pr-4 tabular-nums">{fighter.kills}</td>
                <td className="py-2 pr-4 tabular-nums">{fighter.crits}</td>
                <td className="py-2 pr-4">{fighter.survived ? "Yes" : "No"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function buildReplayPlan(match) {
  const battleLog = match?.battle_log || [];
  const roundTimeline = match?.round_timeline || [];
  const units = [];

  const firstRoundIndex = battleLog.findIndex((line) => /^R\d+:/.test(line));
  const introLines = firstRoundIndex > 0 ? battleLog.slice(0, firstRoundIndex) : battleLog.slice(0, Math.min(2, battleLog.length));
  introLines.forEach((line) => units.push({ type: "intro", line }));

  roundTimeline.forEach((round) => {
    (round.events || []).forEach((event) => {
      units.push({
        type: "event",
        sequence: event.sequence,
        line: event.narrative,
      });
    });
  });

  const verdictLines = battleLog.filter((line) => line.startsWith("Ludus Trias verdict:"));
  verdictLines.forEach((line) => units.push({ type: "verdict", line }));

  return { units };
}

function currentPlaybackLabel(unit) {
  if (!unit) return "Replay ready.";
  if (unit.type === "intro") return "Opening the battle.";
  if (unit.type === "event") return "The arena action continues.";
  if (unit.type === "verdict") return "Final verdict.";
  return "Replay ready.";
}

export const TriumvirateReplayModal = ({ match, open, onClose, userId }) => {
  const [tab, setTab] = useState("timeline");
  const [shown, setShown] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const timelineScrollRef = useRef(null);
  const won = match?.winner_user_id === userId;
  const replayPlan = useMemo(() => buildReplayPlan(match), [match]);

  useEffect(() => {
    if (!open) return;
    setTab("log");
    setShown(replayPlan.units.length > 0 ? 1 : 0);
    setPlaying(replayPlan.units.length > 1);
    setPlaybackSpeed(1);
  }, [open, match?.match_id, replayPlan.units.length]);

  useEffect(() => {
    if (!open || !playing) return;
    if (shown >= replayPlan.units.length) {
      setPlaying(false);
      return;
    }
    const delay = Math.max(250, 1100 / playbackSpeed);
    const timer = window.setTimeout(() => {
      setShown((value) => Math.min(value + 1, replayPlan.units.length));
    }, delay);
    return () => window.clearTimeout(timer);
  }, [open, tab, playing, shown, playbackSpeed, replayPlan.units.length]);

  useEffect(() => {
    if (tab !== "timeline") return;
    if (timelineScrollRef.current) {
      timelineScrollRef.current.scrollTop = timelineScrollRef.current.scrollHeight;
    }
  }, [shown, tab]);

  const tabs = useMemo(() => [
    { id: "scoreboard", label: "Battle Performance" },
    { id: "log", label: "Battle Log" },
  ], []);

  const activeUnit = replayPlan.units[Math.max(0, Math.min(shown - 1, replayPlan.units.length - 1))] || null;
  const replayComplete = shown >= replayPlan.units.length;
  const introUnits = replayPlan.units.slice(0, shown).filter((unit) => unit.type === "intro");
  const verdictUnits = replayPlan.units.slice(0, shown).filter((unit) => unit.type === "verdict");

  const skipToResult = () => {
    setShown(replayPlan.units.length);
    setPlaying(false);
  };

  if (!open || !match) return null;

  return (
    <Modal open={open} onClose={onClose} title="Ludus Trias Replay">
      <div data-testid="triumvirate-battle-result-modal" className="space-y-4">
        <div className={`border rounded-sm p-4 ${won ? "border-emerald-700/40 bg-emerald-950/15" : "border-red-900/40 bg-red-950/15"}`}>
          <div className="flex flex-wrap items-center gap-2">
            <LeagueBadge league={match.league} label={TRIUMVIRATE_MODE.label} testId="triumvirate-battle-league-badge" />
            {match.season_label ? <span data-testid="triumvirate-battle-season-label" className="text-xs uppercase tracking-[0.16em] text-zinc-500">{match.season_label}</span> : null}
          </div>
          <div data-testid="triumvirate-battle-headline" className="mt-3 font-cinzel text-xl text-amber-200">{match.headline}</div>
          <p data-testid="triumvirate-battle-summary" className="mt-2 text-sm text-zinc-300">{match.summary}</p>
          <div className="mt-4 grid gap-3 md:grid-cols-4 text-sm">
            <div className="border border-zinc-800 rounded-sm px-3 py-2 text-zinc-300">
              <div className="text-[11px] uppercase tracking-[0.16em] text-zinc-500">Rounds</div>
              <div data-testid="triumvirate-battle-rounds" className="mt-1 font-semibold">{match.rounds}</div>
            </div>
            <div className="border border-zinc-800 rounded-sm px-3 py-2 text-zinc-300">
              <div className="text-[11px] uppercase tracking-[0.16em] text-zinc-500">Rating</div>
              <div data-testid="triumvirate-battle-rating-delta" className={`mt-1 font-semibold ${won ? "text-emerald-300" : "text-red-300"}`}>Rank {deltaText(match.my_rating_delta || 0)}</div>
            </div>
            <div className="border border-zinc-800 rounded-sm px-3 py-2 text-zinc-300">
              <div className="text-[11px] uppercase tracking-[0.16em] text-zinc-500">Attacker average</div>
              <div className="mt-1 font-semibold">{Number(match.attacker_score || 0).toLocaleString()}</div>
            </div>
            <div className="border border-zinc-800 rounded-sm px-3 py-2 text-zinc-300">
              <div className="text-[11px] uppercase tracking-[0.16em] text-zinc-500">Defender average</div>
              <div className="mt-1 font-semibold">{Number(match.defender_score || 0).toLocaleString()}</div>
            </div>
          </div>
        </div>

        <div data-testid="triumvirate-battle-replay-tabs" className="flex flex-wrap gap-2">
          {tabs.map((entry) => (
            <button
              key={entry.id}
              type="button"
              data-testid={`triumvirate-replay-tab-${entry.id}`}
              onClick={() => setTab(entry.id)}
              className={tab === entry.id ? btn() : btn("ghost")}
            >
              {entry.label}
            </button>
          ))}
        </div>

        {(["timeline", "log"].includes(tab)) && replayPlan.units.length > 0 && (
          <div data-testid="triumvirate-replay-controls" className="border border-zinc-800 bg-zinc-950/60 rounded-sm px-4 py-3 flex flex-wrap items-center justify-between gap-3">
            <div className="space-y-1 min-w-[220px] flex-1">
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Replay Playback</div>
              <div data-testid="triumvirate-replay-status" className="text-sm text-zinc-300">
                {replayComplete ? "Replay complete." : currentPlaybackLabel(activeUnit)}
              </div>
            </div>
            <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap justify-start sm:justify-end">
              <button
                type="button"
                data-testid="triumvirate-replay-play-pause"
                onClick={() => setPlaying((value) => !value)}
                className={btn("ghost") + " px-3 py-2"}
              >
                {playing ? <Pause size={14} /> : <Play size={14} />} <span>{playing ? "Pause" : replayComplete ? "Replay Ended" : "Play"}</span>
              </button>
              <button
                type="button"
                data-testid="triumvirate-replay-skip-result"
                onClick={skipToResult}
                disabled={replayComplete}
                className={`${btn("ghost")} px-3 py-2 ${replayComplete ? "opacity-50 cursor-not-allowed" : ""}`}
              >
                <FastForward size={14} /> <span>Skip to Result</span>
              </button>
              <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap justify-start sm:justify-end">
                <Gauge size={14} className="text-zinc-500" />
                {[1, 2, 3, 4].map((speed) => (
                  <button
                    key={speed}
                    type="button"
                    data-testid={`triumvirate-replay-speed-${speed}`}
                    onClick={() => setPlaybackSpeed(speed)}
                    className={`px-2 py-1 border rounded-sm text-[11px] uppercase tracking-[0.18em] transition-colors ${playbackSpeed === speed ? "border-amber-500 bg-amber-500/10 text-amber-200" : "border-zinc-800 text-zinc-400 hover:border-zinc-700 hover:text-zinc-200"}`}
                  >
                    {speed}x
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {tab === "timeline" && false && (
          <div ref={timelineScrollRef} data-testid="triumvirate-battle-timeline" className="space-y-4 max-h-[32rem] overflow-y-auto pr-1">
            {replayPlan.units.length === 0 ? (
              <Empty label="Replay timeline will appear for new Ludus Trias matches." />
            ) : (
              <>
                {introUnits.length > 0 && (
                  <div data-testid="triumvirate-replay-intro" className="border border-zinc-800 bg-zinc-950/45 rounded-sm p-4 space-y-2">
                    {introUnits.map((unit, index) => (
                      <div key={`intro-${index}`} data-testid={`triumvirate-replay-intro-line-${index}`} className="border border-zinc-900 bg-zinc-950/35 px-3 py-2 rounded-sm text-sm text-zinc-300">
                        {unit.line}
                      </div>
                    ))}
                  </div>
                )}

                {replayPlan.units.slice(0, shown).filter((unit) => unit.type === "event").map((unit, index) => <div key={`${unit.sequence}-${index}`} data-testid={`triumvirate-replay-event-${index}`} className="border border-zinc-900 bg-zinc-950/35 px-3 py-2 rounded-sm text-sm text-zinc-300">{unit.line}</div>)}

                {verdictUnits.length > 0 && (
                  <div data-testid="triumvirate-replay-verdict" className="border border-amber-800/40 bg-amber-950/10 rounded-sm p-4 space-y-2">
                    {verdictUnits.map((unit, index) => (
                      <div key={`verdict-${index}`} data-testid={`triumvirate-replay-verdict-line-${index}`} className="font-cinzel text-amber-200">
                        {unit.line}
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {tab === "scoreboard" && (
          <div data-testid="triumvirate-battle-scoreboard" className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <TeamStatsTable title="Attacker performance" fighters={match.team_stats?.attacker || []} testId="triumvirate-team-stats-attacker" />
              <TeamStatsTable title="Defender performance" fighters={match.team_stats?.defender || []} testId="triumvirate-team-stats-defender" />
            </div>
            <div className="grid gap-3 md:grid-cols-2">
              <div className="border border-zinc-800 bg-zinc-950/50 rounded-sm p-4 text-sm text-zinc-300">
                <div className="text-xs uppercase tracking-[0.16em] text-zinc-500">Attacker totals</div>
                <div className="mt-2">Alive: {match.battle_summary?.attacker?.alive || 0}</div>
                <div>Damage dealt: {match.battle_summary?.attacker?.damage_dealt || 0}</div>
                <div>Kills: {match.battle_summary?.attacker?.kills || 0}</div>
              </div>
              <div className="border border-zinc-800 bg-zinc-950/50 rounded-sm p-4 text-sm text-zinc-300">
                <div className="text-xs uppercase tracking-[0.16em] text-zinc-500">Defender totals</div>
                <div className="mt-2">Alive: {match.battle_summary?.defender?.alive || 0}</div>
                <div>Damage dealt: {match.battle_summary?.defender?.damage_dealt || 0}</div>
                <div>Kills: {match.battle_summary?.defender?.kills || 0}</div>
              </div>
            </div>
          </div>
        )}

        {tab === "log" && (
          <div data-testid="triumvirate-battle-log" className="border border-zinc-800 bg-zinc-950/60 p-4 rounded-sm">
            <div className="font-cinzel text-sm uppercase tracking-[0.16em] text-zinc-400">Battle Commentary</div>
            <div className="mt-3" data-testid="triumvirate-replay-log">
              <BattleLogFeed
                lines={(match.battle_log || []).map((line) => line.replace(/^R\d+:\s*/, "").replace(/ in \d+ rounds?\./i, "."))}
                shown={Math.min(shown, (match.battle_log || []).length)}
                loading={playing && shown < (match.battle_log || []).length}
                dataTestId="triumvirate-replay-log-feed"
              />
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};
